/*  MobileView — vista mobile parametrica.
    Riceve proposalId (a/b/c) + role (fo/hk/tech) e renderizza una schermata mobile coerente.
    Tutto inline dentro un frame 390x844 per simulare iPhone.
*/

import React from 'react';
import { PROPOSALS, MOCK_FO, MOCK_HK, MOCK_TECH } from './_mobileShared';
import {
  Plus, Bell, Search, Camera, QrCode, MapPin, ChevronRight, Check,
  AlertTriangle, Sparkles, Wrench, ClipboardList, BedDouble, Phone, Home, ListChecks,
  User as UserIcon
} from 'lucide-react';

/* -------- Helpers per stile dipendente dalla proposta -------- */

const radius = (t) => t.radius || '8px';

const Card = ({ t, children, className = '', style = {}, padding = 'p-4' }) => (
  <div
    className={className + ' ' + padding}
    style={{
      background: t.surface,
      border: t.cardBorder,
      borderRadius: radius(t),
      boxShadow: t.cardShadow,
      ...style,
    }}
  >
    {children}
  </div>
);

const Pill = ({ t, color, bg, children, mono = false }) => (
  <span
    className="text-[10px] px-2 py-0.5 font-semibold"
    style={{
      background: bg,
      color,
      fontFamily: mono ? t.fontMono : t.fontBody,
      borderRadius: t.id === 'a' ? 0 : 999,
      letterSpacing: t.id === 'a' || t.id === 'c' ? '0.06em' : 0,
    }}
  >
    {children}
  </span>
);

const priorityStyle = (t, p) => {
  if (p === 'urgente' || p === 'high')
    return { bg: t.id === 'c' ? 'rgba(239,68,68,0.12)' : '#FBE9E9', fg: t.danger, label: t.id === 'c' ? 'CRIT' : 'Urgente' };
  if (p === 'alta' || p === 'med')
    return { bg: t.id === 'c' ? 'rgba(245,158,11,0.12)' : '#FBEFD9', fg: t.warning, label: t.id === 'c' ? 'HIGH' : 'Alta' };
  return { bg: t.id === 'c' ? 'rgba(148,163,184,0.12)' : t.border, fg: t.muted, label: t.id === 'c' ? 'MED' : 'Media' };
};

const stateStyle = (t, s) => {
  const map = {
    da_pulire: { fg: t.warning, bg: t.id === 'c' ? 'rgba(245,158,11,0.12)' : '#FBEFD9', label: 'Da pulire' },
    pulita: { fg: t.success, bg: t.id === 'c' ? 'rgba(16,185,129,0.12)' : '#E1F4ED', label: 'Pulita' },
    occupata: { fg: t.accent, bg: t.id === 'c' ? 'rgba(59,130,246,0.12)' : (t.id === 'b' ? '#E4D3B8' : '#DBEAFE'), label: 'Occupata' },
    fuori_servizio: { fg: t.danger, bg: t.id === 'c' ? 'rgba(239,68,68,0.12)' : '#FBE9E9', label: 'Fuori servizio' },
    assegnato: { fg: t.muted, bg: t.id === 'c' ? 'rgba(148,163,184,0.12)' : t.border, label: 'Assegnato' },
    in_lavorazione: { fg: t.accent, bg: t.id === 'c' ? 'rgba(59,130,246,0.12)' : (t.id === 'b' ? '#E4D3B8' : '#DBEAFE'), label: 'In lavorazione' },
  };
  return map[s] || { fg: t.muted, bg: t.border, label: s };
};

/* -------- Top bar mobile, varia per proposta -------- */

const MobileHeader = ({ t, title, subtitle, action, role }) => {
  if (t.id === 'c') {
    return (
      <div
        className="px-5 pt-5 pb-3 sticky top-0 z-10"
        style={{ background: t.bg, borderBottom: `1px solid ${t.border}` }}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-[10px]" style={{ fontFamily: t.fontMono, color: t.muted }}>
            <span style={{ color: t.success }}>● LIVE</span>
            <span style={{ color: t.mutedDim }}>·</span>
            <span>{role.toUpperCase()}</span>
          </div>
          <div className="relative">
            <Bell className="w-4 h-4" style={{ color: t.muted }} />
            <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full" style={{ background: t.danger, boxShadow: `0 0 6px ${t.danger}` }} />
          </div>
        </div>
        <h1 className="text-xl font-bold leading-tight" style={{ fontFamily: t.fontDisplay }}>{title}</h1>
        {subtitle && <p className="text-xs mt-1" style={{ color: t.muted }}>{subtitle}</p>}
      </div>
    );
  }
  if (t.id === 'b') {
    return (
      <div className="px-5 pt-6 pb-4" style={{ background: t.bg }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: t.primary }}>
              <Sparkles className="w-3.5 h-3.5" style={{ color: t.accent }} />
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em]" style={{ color: t.muted }}>
              Hostfix · {role}
            </div>
          </div>
          <Bell className="w-4 h-4" style={{ color: t.muted }} />
        </div>
        <h1 className="text-3xl leading-tight" style={{ fontFamily: t.fontDisplay, color: t.primary, fontWeight: 600 }}>
          {title}
        </h1>
        {subtitle && <p className="text-sm mt-1" style={{ color: t.muted }}>{subtitle}</p>}
      </div>
    );
  }
  // a — operational
  return (
    <div className="px-5 pt-5 pb-3 sticky top-0 z-10" style={{ background: t.bg, borderBottom: `1px solid ${t.border}` }}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: t.muted, fontFamily: t.fontMono }}>
          {role} · live
        </div>
        <Bell className="w-4 h-4" style={{ color: t.muted }} />
      </div>
      <h1 className="text-2xl font-bold leading-tight tracking-tight" style={{ fontFamily: t.fontDisplay }}>
        {title}
      </h1>
      {subtitle && <p className="text-xs mt-1" style={{ color: t.muted }}>{subtitle}</p>}
    </div>
  );
};

/* -------- Bottom tab bar mobile -------- */

const TabBar = ({ t, role }) => {
  const tabs = {
    fo: [
      { icon: Home, label: 'Home', active: true },
      { icon: Plus, label: 'Nuovo', highlight: true },
      { icon: ClipboardList, label: 'Miei ticket' },
      { icon: UserIcon, label: 'Profilo' },
    ],
    hk: [
      { icon: BedDouble, label: 'Camere', active: true },
      { icon: AlertTriangle, label: 'Segnala', highlight: true },
      { icon: ListChecks, label: 'Storico' },
      { icon: UserIcon, label: 'Profilo' },
    ],
    tech: [
      { icon: Wrench, label: 'Lavoro', active: true },
      { icon: QrCode, label: 'Scan', highlight: true },
      { icon: ClipboardList, label: 'Coda' },
      { icon: UserIcon, label: 'Profilo' },
    ],
  }[role];

  return (
    <div
      className="absolute bottom-0 left-0 right-0 px-4 py-3 flex items-center justify-around"
      style={{
        background: t.id === 'c' ? 'rgba(18,18,20,0.85)' : t.surface,
        backdropFilter: t.id === 'c' ? 'blur(12px)' : 'none',
        WebkitBackdropFilter: t.id === 'c' ? 'blur(12px)' : 'none',
        borderTop: `1px solid ${t.border}`,
      }}
    >
      {tabs.map((tab, i) => {
        if (tab.highlight) {
          return (
            <button
              key={i}
              className="w-12 h-12 -mt-6 flex items-center justify-center"
              style={{
                background: t.id === 'b' ? t.primary : t.accent,
                color: t.id === 'b' ? t.surface : '#fff',
                borderRadius: t.id === 'a' ? 0 : 999,
                boxShadow: t.id === 'c' ? `0 0 18px ${t.accentGlow}` : '0 4px 12px rgba(0,0,0,0.15)',
              }}
            >
              <tab.icon className="w-5 h-5" />
            </button>
          );
        }
        return (
          <button key={i} className="flex flex-col items-center gap-0.5">
            <tab.icon className="w-4 h-4" style={{ color: tab.active ? t.accent : t.muted }} />
            <span
              className="text-[9px]"
              style={{
                color: tab.active ? t.text : t.muted,
                fontFamily: t.fontBody,
                fontWeight: tab.active ? 600 : 400,
              }}
            >
              {tab.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

/* -------- View: FRONT OFFICE -------- */

const FOView = ({ t }) => (
  <>
    <MobileHeader
      t={t}
      role="Front Office"
      title={`Ciao, ${MOCK_FO.user.split(' ')[0]}.`}
      subtitle={`${MOCK_FO.myOpen.length} ticket aperti che hai segnalato`}
    />

    <div className="px-5 pt-4 pb-24 space-y-4">
      {/* CTA Nuovo ticket */}
      <Card t={t} padding="p-4">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 flex items-center justify-center"
            style={{
              background: t.accent,
              color: '#fff',
              borderRadius: t.id === 'a' ? 0 : 999,
            }}
          >
            <Plus className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold" style={{ fontFamily: t.fontDisplay }}>Apri un ticket</div>
            <div className="text-xs" style={{ color: t.muted }}>Foto, camera, descrizione · 30 secondi</div>
          </div>
          <ChevronRight className="w-4 h-4" style={{ color: t.muted }} />
        </div>
      </Card>

      {/* KPI personali */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Aperti', val: MOCK_FO.myOpen.length, color: t.warning },
          { label: 'In corso', val: 2, color: t.accent },
          { label: 'Risolti oggi', val: 5, color: t.success },
        ].map((k) => (
          <Card t={t} key={k.label} padding="p-3">
            <div className="text-[10px] uppercase tracking-wider" style={{ color: t.muted, fontFamily: t.fontMono }}>{k.label}</div>
            <div className="text-2xl font-bold mt-1 tabular-nums" style={{ color: k.color, fontFamily: t.fontDisplay }}>{k.val}</div>
          </Card>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2 px-1">
          <h2 className="text-sm font-semibold" style={{ fontFamily: t.fontDisplay }}>I miei ticket aperti</h2>
          <span className="text-xs" style={{ color: t.accent }}>Tutti →</span>
        </div>
        <div className="space-y-2">
          {MOCK_FO.myOpen.map((tk) => {
            const s = stateStyle(t, tk.status);
            return (
              <Card t={t} key={tk.id} padding="p-3">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] tabular-nums" style={{ color: t.muted, fontFamily: t.fontMono }}>{tk.id}</span>
                      <span className="text-[10px]" style={{ color: t.muted, fontFamily: t.fontMono }}>· cam {tk.room}</span>
                    </div>
                    <div className="text-sm font-medium truncate">{tk.title}</div>
                  </div>
                  <div className="text-right">
                    <Pill t={t} bg={s.bg} color={s.fg}>{s.label}</Pill>
                    <div className="text-[10px] mt-1" style={{ color: t.muted, fontFamily: t.fontMono }}>{tk.age}</div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      <div>
        <h2 className="text-xs uppercase tracking-wider mb-2 px-1" style={{ color: t.muted, fontFamily: t.fontMono }}>
          Risolti di recente
        </h2>
        <div className="space-y-1.5">
          {MOCK_FO.closed.map((tk) => (
            <div
              key={tk.id}
              className="flex items-center gap-3 px-3 py-2 text-xs"
              style={{
                background: t.id === 'c' ? 'transparent' : 'transparent',
                opacity: 0.7,
              }}
            >
              <Check className="w-3.5 h-3.5" style={{ color: t.success }} />
              <span className="flex-1 truncate">{tk.title} · cam {tk.room}</span>
              <span style={{ color: t.muted }}>{tk.closedAt}</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    <TabBar t={t} role="fo" />
  </>
);

/* -------- View: HOUSEKEEPING -------- */

const HKView = ({ t }) => (
  <>
    <MobileHeader
      t={t}
      role="Housekeeping"
      title={`${MOCK_HK.doneRooms}/${MOCK_HK.todayRooms} camere oggi`}
      subtitle={`Buon lavoro, ${MOCK_HK.user.split(' ')[0]}`}
    />

    <div className="px-5 pt-4 pb-24 space-y-4">
      {/* Progress bar */}
      <Card t={t}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-medium" style={{ fontFamily: t.fontBody }}>Avanzamento giornata</div>
          <div className="text-xs tabular-nums" style={{ color: t.muted, fontFamily: t.fontMono }}>
            {Math.round((MOCK_HK.doneRooms / MOCK_HK.todayRooms) * 100)}%
          </div>
        </div>
        <div className="h-2" style={{ background: t.id === 'c' ? t.surface2 : t.border, borderRadius: t.id === 'a' ? 0 : 999 }}>
          <div
            className="h-full transition-all"
            style={{
              width: `${(MOCK_HK.doneRooms / MOCK_HK.todayRooms) * 100}%`,
              background: t.accent,
              borderRadius: t.id === 'a' ? 0 : 999,
              boxShadow: t.id === 'c' ? `0 0 8px ${t.accentGlow}` : 'none',
            }}
          />
        </div>
        <div className="flex items-center gap-3 mt-3 text-[10px]" style={{ color: t.muted, fontFamily: t.fontMono }}>
          <span><span style={{ color: t.success }}>●</span> Pulita 5</span>
          <span><span style={{ color: t.warning }}>●</span> Da pulire 6</span>
          <span><span style={{ color: t.accent }}>●</span> Occupata 2</span>
          <span><span style={{ color: t.danger }}>●</span> FS 1</span>
        </div>
      </Card>

      {/* Quick action */}
      <Card t={t} padding="p-4">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 flex items-center justify-center"
            style={{
              background: t.danger,
              color: '#fff',
              borderRadius: t.id === 'a' ? 0 : 999,
            }}
          >
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold" style={{ fontFamily: t.fontDisplay }}>Segnala problema</div>
            <div className="text-xs" style={{ color: t.muted }}>Apri ticket manutenzione per la camera</div>
          </div>
          <ChevronRight className="w-4 h-4" style={{ color: t.muted }} />
        </div>
      </Card>

      <div>
        <h2 className="text-sm font-semibold mb-2 px-1" style={{ fontFamily: t.fontDisplay }}>Camere assegnate</h2>
        <div className="space-y-2">
          {MOCK_HK.rooms.map((r) => {
            const s = stateStyle(t, r.state);
            return (
              <Card t={t} key={r.num} padding="p-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 flex items-center justify-center font-bold tabular-nums"
                    style={{
                      background: s.bg,
                      color: s.fg,
                      borderRadius: t.id === 'a' ? 0 : (t.id === 'b' ? 14 : 8),
                      fontFamily: t.fontDisplay,
                    }}
                  >
                    {r.num}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.guest}</div>
                    <div className="text-[10px] mt-0.5" style={{ color: s.fg, fontFamily: t.fontMono }}>
                      {s.label.toUpperCase()}
                    </div>
                  </div>
                  <button
                    className="w-8 h-8 flex items-center justify-center"
                    style={{
                      background: t.id === 'c' ? t.surface2 : t.border,
                      borderRadius: t.id === 'a' ? 0 : 999,
                    }}
                  >
                    <Check className="w-4 h-4" style={{ color: t.muted }} />
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>

    <TabBar t={t} role="hk" />
  </>
);

/* -------- View: TECNICO -------- */

const TechView = ({ t }) => {
  const p = priorityStyle(t, MOCK_TECH.next.priority);
  return (
    <>
      <MobileHeader
        t={t}
        role="Tecnico"
        title={`Buon turno, ${MOCK_TECH.user.split(' ')[0]}.`}
        subtitle={`${MOCK_TECH.done}/${MOCK_TECH.today} ticket chiusi · ${MOCK_TECH.queue.length} in coda`}
      />

      <div className="px-5 pt-4 pb-24 space-y-4">
        {/* Next ticket — focus card */}
        <Card
          t={t}
          padding="p-0"
          style={{
            background: t.id === 'b' ? `linear-gradient(135deg, ${t.primary} 0%, #1e2d2a 100%)` : (t.id === 'c' ? t.surface2 : t.surface),
            color: t.id === 'b' ? t.surface : t.text,
            border: t.id === 'b' ? 'none' : t.cardBorder,
          }}
        >
          <div className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-widest" style={{ color: t.id === 'b' ? t.accent : t.accent, fontFamily: t.fontMono }}>
                Prossimo intervento
              </div>
              <Pill t={t} bg={p.bg} color={p.fg} mono>{p.label}</Pill>
            </div>
            <h2 className="text-xl font-semibold leading-tight" style={{ fontFamily: t.fontDisplay }}>
              {MOCK_TECH.next.title}
            </h2>
            <div className="flex items-center gap-3 mt-2 text-xs" style={{ color: t.id === 'b' ? 'rgba(255,255,255,0.7)' : t.muted }}>
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Cam {MOCK_TECH.next.room} · {MOCK_TECH.next.floor}</span>
              <span style={{ fontFamily: t.fontMono }}>{MOCK_TECH.next.age} fa</span>
            </div>
            <div
              className="mt-3 p-3 text-xs"
              style={{
                background: t.id === 'b' ? 'rgba(255,255,255,0.08)' : (t.id === 'c' ? t.surface : t.bg),
                borderRadius: t.id === 'a' ? 0 : 10,
                color: t.id === 'b' ? 'rgba(255,255,255,0.85)' : t.text,
              }}
            >
              {MOCK_TECH.next.note}
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              <button
                className="py-2.5 text-xs font-semibold flex items-center justify-center gap-1"
                style={{
                  background: t.accent,
                  color: '#fff',
                  borderRadius: t.id === 'a' ? 0 : 8,
                  fontFamily: t.fontBody,
                }}
              >
                Inizia
              </button>
              <button
                className="py-2.5 text-xs font-semibold flex items-center justify-center gap-1"
                style={{
                  background: t.id === 'b' ? 'rgba(255,255,255,0.12)' : (t.id === 'c' ? t.surface2 : t.surface),
                  border: t.id !== 'b' ? `1px solid ${t.border}` : 'none',
                  borderRadius: t.id === 'a' ? 0 : 8,
                  color: t.id === 'b' ? '#fff' : t.text,
                }}
              >
                <Camera className="w-3.5 h-3.5" /> Foto
              </button>
              <button
                className="py-2.5 text-xs font-semibold flex items-center justify-center gap-1"
                style={{
                  background: t.id === 'b' ? 'rgba(255,255,255,0.12)' : (t.id === 'c' ? t.surface2 : t.surface),
                  border: t.id !== 'b' ? `1px solid ${t.border}` : 'none',
                  borderRadius: t.id === 'a' ? 0 : 8,
                  color: t.id === 'b' ? '#fff' : t.text,
                }}
              >
                <Phone className="w-3.5 h-3.5" /> FO
              </button>
            </div>
          </div>
        </Card>

        {/* Coda */}
        <div>
          <div className="flex items-center justify-between mb-2 px-1">
            <h2 className="text-sm font-semibold" style={{ fontFamily: t.fontDisplay }}>In coda</h2>
            <button
              className="text-xs flex items-center gap-1 px-2 py-1"
              style={{
                background: t.id === 'c' ? t.surface2 : t.surface,
                border: `1px solid ${t.border}`,
                borderRadius: t.id === 'a' ? 0 : 999,
                color: t.accent,
              }}
            >
              <QrCode className="w-3 h-3" /> Scan camera
            </button>
          </div>
          <div className="space-y-2">
            {MOCK_TECH.queue.map((q) => {
              const pp = priorityStyle(t, q.priority);
              return (
                <Card t={t} key={q.id} padding="p-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] tabular-nums" style={{ color: t.muted, fontFamily: t.fontMono }}>{q.id}</span>
                        <Pill t={t} bg={pp.bg} color={pp.fg} mono>{pp.label}</Pill>
                      </div>
                      <div className="text-sm font-medium truncate">{q.title}</div>
                      <div className="text-[10px] mt-0.5" style={{ color: t.muted, fontFamily: t.fontMono }}>
                        cam {q.room} · {q.age}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4" style={{ color: t.muted }} />
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      <TabBar t={t} role="tech" />
    </>
  );
};

/* -------- Frame iPhone + entry component -------- */

const PhoneFrame = ({ children, t, label }) => (
  <div className="flex flex-col items-center gap-2">
    <div className="text-[10px] uppercase tracking-widest text-neutral-500" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
      {label}
    </div>
    <div
      className="relative"
      style={{
        width: 320,
        height: 660,
        borderRadius: 38,
        padding: 8,
        background: '#0c0c0c',
        boxShadow: '0 24px 50px -12px rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.05)',
      }}
    >
      <div
        className="relative overflow-hidden"
        style={{
          width: '100%',
          height: '100%',
          borderRadius: 30,
          background: t.bg,
          color: t.text,
          fontFamily: t.fontBody,
        }}
      >
        {/* Notch */}
        <div
          className="absolute top-1.5 left-1/2 -translate-x-1/2 w-24 h-5 rounded-full z-30"
          style={{ background: '#0c0c0c' }}
        />
        <div className="absolute inset-0 overflow-y-auto pt-3">{children}</div>
      </div>
    </div>
  </div>
);

const MobileView = ({ proposalId, role }) => {
  const t = PROPOSALS[proposalId] || PROPOSALS.a;
  const label = `Proposta ${proposalId.toUpperCase()} · ${t.name}`;
  const view = role === 'hk' ? <HKView t={t} /> : role === 'tech' ? <TechView t={t} /> : <FOView t={t} />;
  return (
    <PhoneFrame t={t} label={label}>
      {view}
    </PhoneFrame>
  );
};

export default MobileView;
